'use client';

import React from 'react';
import Image from 'next/image';
import { CompanyValueType } from '@/types/company-detail';
import Stack from '@mui/material/Stack';
import BreadcrumbNav from '@/components/seo/BreadcrumbNav';
import OutlinedTag from '../OutlinedTag';
import SvgMoney from './SvgMoney';
import SvgBuilding from './SvgBuilding';
import SvgBubble from './SvgBubble';
import SvgFinancing from './SvgFinancing';
import SvgLocation from './SvgLocation';
import SvgNewsMode from './SvgNewsMode';
import SvgHot from './SvgHot';
import SvgJob from './SvgJob';
import {
  COMPANY_ROUTER_PATH,
  HOME_PAGE_ROUTER_PATH,
} from '@/constants/router-path';
import { COMPANY_SIZE_MAP } from '@/constants/company-detail';
import { isEmpty } from '@/utils/seo-content';

const INDICATOR_ORDER: Array<[label: string, Icon: React.FC]> = [
  ['营收规模', SvgMoney],
  ['员工规模', SvgBuilding],
  ['开放岗位', SvgJob],
  ['行业地位', SvgHot],
  ['总部基地', SvgLocation],
  ['成立时间', SvgNewsMode],
  ['融资阶段', SvgFinancing],
  ['业务覆盖', SvgBubble],
];

const HeroSection = (props: { companyDetail: CompanyValueType }) => {
  const { companyDetail } = props;
  const {
    id: companyId,
    name: companyName,
    size: companySize,
    logo,
    description,
    seo_content: seoContent,
    job_opportunity: jobOpportunity = [],
  } = companyDetail;
  const cardData = seoContent['公司卡片'] || {};

  const indicatorMap = React.useMemo(() => {
    const dict: Record<string, any> = {};
    (cardData['核心指标'] ?? []).forEach(
      (it: any) => (dict[it['指标名称']] = it),
    );
    return dict;
  }, [cardData]);

  const cardsToShow = INDICATOR_ORDER.flatMap(([label, Icon]) => {
    if (label === '员工规模' && companySize) {
      return [
        {
          label,
          value: COMPANY_SIZE_MAP[companySize],
          desc: '基于公开信息',
          Icon,
        },
      ];
    }
    const item = indicatorMap[label];
    if (!item) return [];
    const val = item['指标值'];
    if (isEmpty(val)) return [];
    return [{ label, value: val, desc: item['补充说明'], Icon }];
  });
  const breadcrumbList = [
    { href: HOME_PAGE_ROUTER_PATH, title: '薪酬数据' },
    { href: `${COMPANY_ROUTER_PATH}${companyId}/`, title: companyName },
  ];

  return (
    <section className="flex justify-between gap-20 line-none">
      <div>
        <BreadcrumbNav breadcrumbList={breadcrumbList} />
        <Stack spacing={2} className="mt-8">
          <div className="flex gap-2">
            {jobOpportunity.length > 0 && <OutlinedTag>热招中</OutlinedTag>}
            <OutlinedTag>
              需求量{jobOpportunity.length > 5 ? '大' : '小'}
            </OutlinedTag>
          </div>
          <div className="flex items-center gap-2 font-semibold">
            {logo && (
              <div>
                <Image
                  src={logo}
                  alt={`${companyName}-logo`}
                  width={48}
                  height={48}
                />
              </div>
            )}
            <h1 className="text-3.25xl">{`${companyName}招聘`}</h1>
          </div>
          <div className="line-clamp-3" title={description ?? ''}>
            {description}
          </div>
          {cardData['公司页标签']?.length > 0 && (
            <div className="flex gap-x-2">
              {cardData['公司页标签'].map((tag: string, index: number) => (
                <OutlinedTag key={index}>{tag}</OutlinedTag>
              ))}
            </div>
          )}
        </Stack>
      </div>
      <div
        className={`grid grid-cols-[260px_260px] gap-6 self-center ${cardsToShow.length > 2 ? 'grid-rows-[127px_127px]' : 'grid-rows-[127px]'}`}
      >
        {cardsToShow
          .filter((item) => item.value)
          .slice(0, 4)
          .map(({ label, desc, value, Icon }, index) => {
            return (
              <div className="bg-white py-4 pl-6 pr-5 rounded-lg" key={index}>
                <div className="flex justify-between items-center font-semibold text-xs text-web-grey-600">
                  <span>{label}</span>
                  <Icon />
                </div>
                <div className="mt-4">
                  <div
                    className="text-2xl font-semibold h-[34px] mb-1 line-clamp-1"
                    title={value}
                  >
                    {value}
                  </div>
                  <div className="text-xs text-web-grey-600">{desc}</div>
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
};
export default HeroSection;
